/**
 * Patient Lab Service
 * API methods for patient portal (session-authenticated)
 */

import api from './api';

// ========================================
// TYPES
// ========================================

export interface PatientLabOrder {
  id: string;
  status: string;
  collectionMethod: 'walk_in' | 'at_home_phlebotomy';
  patientDetails: {
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
    dob?: string;
    gender?: string;
  };
  patientAddress: {
    firstLine?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  tests: string[];
  panel: {
    id: string;
    name: string;
  } | null;
  requisitionUrl?: string;
  createdAt: string;
  updatedAt: string;
  metadata?: Record<string, unknown>;
}

export interface LabCenter {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone?: string;
  distance?: number;
  hours?: string;
  latitude?: number;
  longitude?: number;
}

export interface PatientLabResult {
  id: string;
  testName: string;
  status: string;
  collectionDate?: string;
  resultDate?: string;
  markers: Array<{
    name: string;
    value: number | string;
    unit: string;
    referenceRange?: {
      min?: number;
      max?: number;
      text?: string;
    };
    interpretation?: 'normal' | 'high' | 'low' | 'critical';
  }>;
  createdAt: string;
  loincCode?: string;
  loincDisplay?: string;
  interpretation?: 'normal' | 'high' | 'low' | 'critical';
  referenceRange?: {
    min?: number;
    max?: number;
    text?: string;
  };
}

// ========================================
// HELPER FUNCTIONS
// ========================================

const getHeaders = (sessionToken: string) => ({
  'x-session-token': sessionToken,
});

// ========================================
// API METHODS
// ========================================

/**
 * Get order details for patient portal
 */
export const getOrderDetails = async (
  orderId: string,
  sessionToken: string
): Promise<PatientLabOrder> => {
  const response = await api.get(`/labs/public/order/${orderId}`, {
    headers: getHeaders(sessionToken),
  });
  return response.data.order;
};

/**
 * Search lab centers for patient portal
 */
export const searchCentersForOrder = async (
  orderId: string,
  zipCode: string,
  sessionToken: string,
  radius?: number
): Promise<LabCenter[]> => {
  const params: Record<string, string | number> = { zipCode };
  if (radius) params.radius = radius;

  const response = await api.get(`/labs/public/order/${orderId}/centers`, {
    params,
    headers: getHeaders(sessionToken),
  });
  return response.data.centers || [];
};

/**
 * Book appointment for order (patient portal)
 */
export const bookAppointmentForOrder = async (
  orderId: string,
  bookingKey: string,
  sessionToken: string,
  address?: {
    firstLine?: string;
    city?: string;
    state?: string;
    zip?: string;
  }
): Promise<PatientLabOrder> => {
  const response = await api.post(
    `/labs/public/order/${orderId}/book-appointment`,
    { bookingKey, address },
    { headers: getHeaders(sessionToken) }
  );
  return response.data.order;
};

/**
 * Get results for patient portal
 */
export const getOrderResults = async (
  orderId: string,
  sessionToken: string
): Promise<PatientLabResult[]> => {
  const response = await api.get(`/labs/public/order/${orderId}/results`, {
    headers: getHeaders(sessionToken),
  });
  return response.data.results || [];
};
